import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Users, Search, Filter, Download, Plus, Ban, CheckCircle, Eye,
    Loader2, Calendar, User, ChevronLeft, ChevronRight,
    Store
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { adminApi } from '@/services/api/admin';
import { UserData, DateFilter } from '@/services/types/domain';
import { getUserTypeIcon, getUserStatusIcon } from '@/utils/renderIcons';
import { getUserStatusColor, getUserTypeColor, tUserStatus, tUserType } from '@/utils/formatters';
import AdminCreateUserModal from './AdminCreateUserModal';
import { Link } from 'react-router-dom';

interface AdminUsersManagementProps {
    onNavigateBack?: () => void;
}


interface Filters {
    user_type?: 'customer' | 'seller' | 'admin';
    status?: 'active' | 'inactive' | 'suspended' | 'banned';
    search?: string;
    page?: number;
    per_page?: number;
    // nuevos
    date_from?: string;
    date_to?: string;
}

const formatDateParam = (d: Date) => {
    // YYYY-MM-DD (local)
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const getRangeByFilter = (f: DateFilter): { date_from?: string; date_to?: string } => {
    if (f === 'all') return {};
    const now = new Date();
    const end = formatDateParam(now);

    if (f === 'today') {
        const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        return { date_from: formatDateParam(start), date_to: end };
    }

    if (f === 'week') {
        // Semana desde Lunes (0=Domingo → 1=Lunes)
        const dow = now.getDay(); // 0..6
        const offsetToMonday = (dow + 6) % 7; // convierte Dom(0)→6, Lun(1)→0, ...
        const monday = new Date(now);
        monday.setDate(now.getDate() - offsetToMonday);
        monday.setHours(0, 0, 0, 0);
        return { date_from: formatDateParam(monday), date_to: end };
    }

    // month
    const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    return { date_from: formatDateParam(firstOfMonth), date_to: end };
};

const AdminUsersManagementComponent: React.FC<AdminUsersManagementProps> = () => {
    const [users, setUsers] = useState<UserData[]>([]);
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 10,
        total: 0,
        last_page: 1,
        from: 0,
        to: 0
    });
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [userTypeFilter, setUserTypeFilter] = useState<'all' | 'customer' | 'seller' | 'admin'>('all');
    const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'suspended' | 'banned'>('all');
    const [dateFilter, setDateFilter] = useState<DateFilter>('all');

    const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);

    const [processingIds, setProcessingIds] = useState<Set<number>>(new Set());
    const searchTimeoutRef = useRef<NodeJS.Timeout>();
    const searchInputRef = useRef<HTMLInputElement>(null);

    const fetchUsers = useCallback(async (filters: Filters = {}) => {
        setIsLoading(true);
        try {
            // Tip: si el servicio no soporta tipado con date_from/date_to,
            // este cast mantiene TS feliz y los envía como query params.
            const response = await (adminApi.getUsers as any)({
                user_type: filters.user_type,
                status: filters.status,
                search: filters.search,
                page: filters.page || 1,
                per_page: filters.per_page || 10,
                date_from: filters.date_from,
                date_to: filters.date_to
            });

            setUsers(response.data);
            setPagination({
                current_page: response.meta.current_page,
                per_page: response.meta.per_page,
                total: response.meta.total,
                last_page: response.meta.last_page,
                from: response.meta.from,
                to: response.meta.to
            });
        } catch (error) {
            console.error('Error loading users:', error);
            toast.error('Error al cargar usuarios');
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Inicial + filtros tipo/estado/fecha
    useEffect(() => {
        const { date_from, date_to } = getRangeByFilter(dateFilter);
        fetchUsers({
            user_type: userTypeFilter !== 'all' ? userTypeFilter : undefined,
            status: statusFilter !== 'all' ? statusFilter : undefined,
            page: 1,
            date_from,
            date_to
        });
    }, [userTypeFilter, statusFilter, dateFilter, fetchUsers]);

    // Debounce de búsqueda (incluye filtros actuales)
    useEffect(() => {
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

        searchTimeoutRef.current = setTimeout(() => {
            const { date_from, date_to } = getRangeByFilter(dateFilter);
            if (searchTerm.trim() !== '') {
                fetchUsers({
                    search: searchTerm,
                    user_type: userTypeFilter !== 'all' ? userTypeFilter : undefined,
                    status: statusFilter !== 'all' ? statusFilter : undefined,
                    page: 1,
                    date_from,
                    date_to
                });
            } else {
                fetchUsers({
                    user_type: userTypeFilter !== 'all' ? userTypeFilter : undefined,
                    status: statusFilter !== 'all' ? statusFilter : undefined,
                    page: 1,
                    date_from,
                    date_to
                });
            }
        }, 500);

        return () => {
            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        };
    }, [searchTerm, userTypeFilter, statusFilter, dateFilter, fetchUsers]);

    const handleUpdateUserStatus = async (userId: number, newStatus: 'active' | 'inactive' | 'suspended' | 'banned') => {
        setProcessingIds(prev => new Set(prev).add(userId));
        try {
            await adminApi.updateUserStatus(userId, newStatus);
            setUsers(prev => prev.map(user =>
                user.id === userId ? { ...user, status: newStatus as any, updated_at: new Date().toISOString() } : user
            ));
            toast.success(`Usuario ${newStatus === 'active' ? 'activado' : newStatus === 'suspended' ? 'suspendido' : 'actualizado'} exitosamente`);
        } catch (error) {
            console.error('Error updating user status:', error);
            toast.error('Error al actualizar estado del usuario');
        } finally {
            setProcessingIds(prev => {
                const next = new Set(prev);
                next.delete(userId);
                return next;
            });
        }
    };

    const handlePageChange = (page: number) => {
        const { date_from, date_to } = getRangeByFilter(dateFilter);
        fetchUsers({
            user_type: userTypeFilter !== 'all' ? userTypeFilter : undefined,
            status: statusFilter !== 'all' ? statusFilter : undefined,
            search: searchTerm || undefined,
            page,
            per_page: pagination.per_page,
            date_from,
            date_to
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handlePerPageChange = (perPage: number) => {
        const { date_from, date_to } = getRangeByFilter(dateFilter);
        fetchUsers({
            user_type: userTypeFilter !== 'all' ? userTypeFilter : undefined,
            status: statusFilter !== 'all' ? statusFilter : undefined,
            search: searchTerm || undefined,
            page: 1,
            per_page: perPage,
            date_from,
            date_to
        });
    };

    const openDetailModal = (user: UserData) => {
        setSelectedUser(user);
        setShowDetailModal(true);
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return 'Nunca';
        return new Date(dateString).toLocaleDateString('es-PE', {
            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
        });
    };

    const formatCurrency = (amount: number) =>
        new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }).format(amount);

    const PaginationControls = () => (
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-white" >
            <div className="flex items-center space-x-2" >
                <span className="text-sm text-gray-700" >
                    Mostrando {pagination.from} a {pagination.to} de {pagination.total} resultados
                </span>
                < select
                    value={pagination.per_page}
                    onChange={(e) => handlePerPageChange(Number(e.target.value))}
                    className="border border-gray-300 rounded-md px-2 py-1 text-sm"
                >
                    <option value={10}> 10 por página </option>
                    < option value={25} > 25 por página </option>
                    < option value={50} > 50 por página </option>
                    < option value={100} > 100 por página </option>
                </select>
            </div>

            < div className="flex items-center space-x-2" >
                <button
                    onClick={() => handlePageChange(pagination.current_page - 1)}
                    disabled={pagination.current_page === 1}
                    className="p-1 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <ChevronLeft className="w-4 h-4" />
                </button>

                {
                    Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                        const page = pagination.current_page <= 3
                            ? i + 1
                            : Math.min(pagination.current_page + i - 2, pagination.last_page);

                        return (
                            <button
                                key={page}
                                onClick={() => handlePageChange(page)
                                }
                                className={`w-8 h-8 rounded-md text-sm ${pagination.current_page === page
                                    ? 'bg-primary-600 text-white'
                                    : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                                    }`}
                            >
                                {page}
                            </button>
                        );
                    })}

                <button
                    onClick={() => handlePageChange(pagination.current_page + 1)}
                    disabled={pagination.current_page === pagination.last_page}
                    className="p-1 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <ChevronRight className="w-4 h-4" />
                </button>
            </div>
        </div>
    );

    const stats = {
        total: pagination.total,
        customers: users.filter((u) => u.user_type === 'customer').length,
        sellers: users.filter((u) => u.user_type === 'seller').length,
        admins: users.filter((u) => u.user_type === 'admin').length,
        active: users.filter((u) => u.status === 'active').length,
        suspended: users.filter((u) => u.status === 'suspended').length
    };

    if (isLoading && users.length === 0) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center" >
                <div className="text-center" >
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4" > </div>
                    < p className="text-gray-600" > Cargando usuarios...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6" >
            {/* Header */}
            < div className="mb-8" >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between" >
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 flex items-center" >
                            <Users className="w-6 h-6 mr-2" />
                            Gestión de Usuarios
                        </h1>
                        < p className="mt-1 text-gray-600" > Administra todos los usuarios de la plataforma </p>
                    </div>
                    < div className="mt-4 sm:mt-0 flex space-x-3" >
                        <button className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors" >
                            <Download className="w-4 h-4 mr-2" />
                            Exportar
                        </button>
                        < button
                            onClick={() => setShowCreateModal(true)}
                            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Nuevo Usuario
                        </button>
                    </div>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-8" >
                {/* ... (tus tarjetas existentes, sin cambios) ... */}
                < div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" >
                    <div className="flex items-center" >
                        <div className="p-2 bg-blue-100 rounded-lg" > <Users className="w-5 h-5 text-blue-600" /> </div>
                        < div className="ml-3" > <p className="text-xs font-medium text-gray-600" > Total </p><p className="text-lg font-bold text-gray-900">{stats.total}</p > </div>
                    </div>
                </div>
                < div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" >
                    <div className="flex items-center" >
                        <div className="p-2 bg-gray-100 rounded-lg" > <User className="w-5 h-5 text-gray-600" /> </div>
                        < div className="ml-3" > <p className="text-xs font-medium text-gray-600" > Clientes </p><p className="text-lg font-bold text-gray-900">{stats.customers}</p > </div>
                    </div>
                </div>
                < div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" >
                    <div className="flex items-center" >
                        <div className="p-2 bg-blue-100 rounded-lg" > <Users className="w-5 h-5 text-blue-600" /> </div>
                        < div className="ml-3" > <p className="text-xs font-medium text-gray-600" > Vendedores </p><p className="text-lg font-bold text-gray-900">{stats.sellers}</p > </div>
                    </div>
                </div>
                < div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" >
                    <div className="flex items-center" >
                        <div className="p-2 bg-purple-100 rounded-lg" > <Users className="w-5 h-5 text-purple-600" /> </div>
                        < div className="ml-3" > <p className="text-xs font-medium text-gray-600" > Admins </p><p className="text-lg font-bold text-gray-900">{stats.admins}</p > </div>
                    </div>
                </div>
                < div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" >
                    <div className="flex items-center" >
                        <div className="p-2 bg-green-100 rounded-lg" > <CheckCircle className="w-5 h-5 text-green-600" /> </div>
                        < div className="ml-3" > <p className="text-xs font-medium text-gray-600" > Activos </p><p className="text-lg font-bold text-gray-900">{stats.active}</p > </div>
                    </div>
                </div>
                < div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" >
                    <div className="flex items-center" >
                        <div className="p-2 bg-yellow-100 rounded-lg" > <Ban className="w-5 h-5 text-yellow-600" /> </div>
                        < div className="ml-3" > <p className="text-xs font-medium text-gray-600" > Suspendidos </p><p className="text-lg font-bold text-gray-900">{stats.suspended}</p > </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6" >
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4" >
                    <div className="relative" >
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                            ref={searchInputRef}
                            type="text"
                            placeholder="Buscar por nombre, email o usuario..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                    </div>

                    < div className="relative" >
                        <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <select
                            value={userTypeFilter}
                            onChange={(e) => setUserTypeFilter(e.target.value as any)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 appearance-none"
                        >
                            <option value="all" > Todos los tipos </option>
                            < option value="customer" > Clientes </option>
                            < option value="seller" > Vendedores </option>
                            < option value="admin" > Administradores </option>
                        </select>
                    </div>

                    < div className="relative" >
                        <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value as any)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 appearance-none"
                        >
                            <option value="all" > Todos los estados </option>
                            < option value="active" > Activos </option>
                            < option value="inactive" > Inactivos </option>
                            < option value="suspended" > Suspendidos </option>
                            < option value="banned" > Baneados </option>
                        </select>
                    </div>

                    {/* Date Filter (operativo) */}
                    <div className="relative" >
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <select
                            value={dateFilter}
                            onChange={(e) => setDateFilter(e.target.value as DateFilter)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 appearance-none"
                        >
                            <option value="all" > Todas las fechas </option>
                            < option value="today" > Hoy </option>
                            < option value="week" > Esta semana </option>
                            < option value="month" > Este mes </option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Tabla */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" >
                <div className="overflow-x-auto" >
                    <table className="min-w-full divide-y divide-gray-200" >
                        <thead className="bg-gray-50" >
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" > Usuario </th>
                                < th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" > Tipo </th>
                                < th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" > Estado </th>
                                < th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" > Último Login </th>
                                < th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" > Estadísticas </th>
                                < th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" > Acciones </th>
                            </tr>
                        </thead>
                        < tbody className="bg-white divide-y divide-gray-200" >
                            {
                                users.map((user) => (
                                    <tr key={user.id} className="hover:bg-gray-50" >
                                        <td className="px-6 py-4 whitespace-nowrap" >
                                            <div className="flex items-center" >
                                                <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center" >
                                                    <span className="text-primary-600 font-medium text-sm" >
                                                        {(user.first_name?.[0] || '').toUpperCase()}{(user.last_name?.[0] || '').toUpperCase()}
                                                    </span>
                                                </div>
                                                < div className="ml-4" >
                                                    <div className="text-sm font-medium text-gray-900" >
                                                        {user.first_name} {user.last_name}
                                                    </div>
                                                    < div className="text-sm text-gray-500" > {user.email} </div>
                                                    < div className="text-xs text-gray-400" > @{user.username} </div>
                                                </div>
                                            </div>
                                        </td>
                                        < td className="px-6 py-4 whitespace-nowrap" >
                                            <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${getUserTypeColor(user.user_type)}`}>
                                                {getUserTypeIcon(user.user_type)}
                                                < span className="capitalize" > {tUserType(user.user_type || "")} </span>
                                            </span>
                                            {
                                                user.seller_info?.store_name && (
                                                    <div className="text-xs text-gray-500 mt-1" > {user.seller_info.store_name} </div>
                                                )
                                            }
                                        </td>
                                        < td className="px-6 py-4 whitespace-nowrap" >
                                            <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium border ${getUserStatusColor(user.status)}`}>
                                                {getUserStatusIcon(user.status)}
                                                < span className="capitalize" > {tUserStatus(user.status || "")} </span>
                                            </span>
                                            {
                                                !user.email_verified_at && (
                                                    <div className="text-xs text-red-500 mt-1" > Email no verificado </div>
                                                )
                                            }
                                        </td>
                                        < td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" >
                                            {formatDate(user.last_login_at)}
                                        </td>
                                        < td className="px-6 py-4 whitespace-nowrap" >
                                            <div className="text-sm text-gray-900" > {user.total_orders || 0} órdenes </div>
                                            < div className="text-sm text-gray-500" >
                                                {formatCurrency(user.total_spent || 0)} gastado
                                            </div>
                                        </td>
                                        < td className="px-6 py-4 whitespace-nowrap text-sm font-medium" >
                                            <div className="flex space-x-2" >
                                                <button
                                                    onClick={() => openDetailModal(user)}
                                                    className="text-primary-600 hover:text-primary-700 p-1 rounded hover:bg-primary-50"
                                                    title="Ver detalles"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </button>

                                                {
                                                    user.status === 'active' && user.user_type !== 'admin' && (
                                                        <button
                                                            onClick={() => handleUpdateUserStatus(user.id, 'suspended')}
                                                            disabled={processingIds.has(user.id)}
                                                            className="text-yellow-600 hover:text-yellow-700 p-1 rounded hover:bg-yellow-50 disabled:opacity-50"
                                                            title="Suspender"
                                                        >
                                                            {processingIds.has(user.id) ? <Loader2 className="w-4 h-4 animate-spin" /> : '⛔'}
                                                        </button>
                                                    )
                                                }

                                                {
                                                    user.status === 'suspended' && (
                                                        <button
                                                            onClick={() => handleUpdateUserStatus(user.id, 'active')}
                                                            disabled={processingIds.has(user.id)}
                                                            className="text-green-600 hover:text-green-700 p-1 rounded hover:bg-green-50 disabled:opacity-50"
                                                            title="Activar"
                                                        >
                                                            {processingIds.has(user.id) ? <Loader2 className="w-4 h-4 animate-spin" /> : '✅'}
                                                        </button>
                                                    )
                                                }
                                                {user.user_type === 'seller' && (
                                                    <Link
                                                        //to={`/admin/users/${user.seller_info?.id}/store`}
                                                        to={`/admin/sellers/${user.seller_id}`}
                                                        className="text-indigo-600 hover:text-indigo-700 p-1 rounded hover:bg-indigo-50"
                                                        title="Ver tienda del vendedor"
                                                    >
                                                        <Store className="w-4 h-4" />
                                                    </Link>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </div>

                {
                    users.length === 0 && !isLoading && (
                        <div className="text-center py-12" >
                            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2" > No hay usuarios </h3>
                            < p className="text-gray-500" >
                                {searchTerm || userTypeFilter !== 'all' || statusFilter !== 'all' || dateFilter !== 'all'
                                    ? 'No se encontraron usuarios que coincidan con los filtros'
                                    : 'No hay usuarios registrados'
                                }
                            </p>
                        </div>
                    )}

                {users.length > 0 && <PaginationControls />}
            </div>

            {/* Detail Modal (igual que lo tenías) */}
            {
                showDetailModal && selectedUser && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" >
                        {/* ... tu modal de detalles sin cambios ... */}
                        {/* Para brevedad, lo omitimos aquí; conserva tu implementación actual */}
                    </div>
                )
            }

            {/* Create User Modal */}
            {
                showCreateModal && (
                    <AdminCreateUserModal
                        open={showCreateModal}
                        onClose={() => setShowCreateModal(false)
                        }
                        onCreated={() => {
                            // refrescar lista a primera página respetando filtros actuales
                            const { date_from, date_to } = getRangeByFilter(dateFilter);
                            fetchUsers({
                                user_type: userTypeFilter !== 'all' ? userTypeFilter : undefined,
                                status: statusFilter !== 'all' ? statusFilter : undefined,
                                search: searchTerm || undefined,
                                page: 1,
                                per_page: pagination.per_page,
                                date_from,
                                date_to
                            });
                        }}
                    />
                )}
        </div>
    );
};

export default AdminUsersManagementComponent;
